import {useDispatch, useSelector} from "react-redux";
import {selectDetailsMapForJobNames} from "../job/selectors";
import {objEquals} from "../../utilities/helperFunctions";
import {selectFilteredQueueJobs} from "../job/JobQueueDisplay";
import ParameterModel from "../../models/library/ParameterModel";
import {applicationFeatures, details, jobStates} from "../../utilities/constants";
import {useTranslation} from "react-i18next";
import React, {useCallback, useLayoutEffect} from "react";
import GuidedJobModel from "../../models/guidedjob/GuidedJobModel";
import JobModel from "../../models/job/JobModel";
import JobQueueHeader from "../job/JobQueueHeader";
import LoadingWrapper from "../common/LoadingWrapper/LoadingWrapper";
import {TableLabelCell, TableLabelHeader, TableRow} from "../common/CustomTable/CustomTable";
import JobTablet from "../job/JobTablet";
import Text from "../common/Text/Text";
import {JobStatusIcons} from "../userSettings/JobCardDesigner/helpers";
import JobVaultModel from "../../models/job/JobVaultModel";
import VaultGuidedJobForm from "./VaultGuidedJobForm";

function useVaultJobs() {
    const jobQueueDisplay = useSelector(state => state.componentStates.jobQueueDisplay);
    const detailsMap = useSelector(state => ({
        job: state.jobDetailsMap,
        ...selectDetailsMapForJobNames(state),
    }), objEquals);

    const jobs = selectFilteredQueueJobs({detailsMap, jobQueueDisplay});
    const jobIdsToArchive = [];
    const vaultJobs = jobs.filter(job => {
        if (Array.isArray(job.sessionParameters)) {
            if (job.sessionParameters.some(param => param.parameterType === ParameterModel.Type.VAULT_SERVICE)) {
                if (jobStates.completed.includes(job.executionState)) {
                    jobIdsToArchive.push(job.id);
                }
                return true;
            }
        }
    });
    vaultJobs.sort((p1, p2) => p2.submittedDateUtc - p1.submittedDateUtc);
    return [vaultJobs, jobIdsToArchive];
}

function VaultJobDisplay() {
    const {t} = useTranslation(['guidedJob', 'common']);
    const dispatch = useDispatch();

    const [vaultJobs, jobsToArchive] = useVaultJobs();
    const activeJob = useSelector(state => state.jobDetailsMap.get(state.componentStates.jobPage.jobId));

    const isLoading = useSelector(state => !state.hasLoaded[details.QUEUE_JOBS]);
    const isGuidedJobFormActive = useSelector(state => state.guidedJobForm.isActive);

    // Active display
    useLayoutEffect(() => {
        dispatch(JobVaultModel.componentActionCreators.setDisplayActive());
        return () => dispatch(JobVaultModel.componentActionCreators.setDisplayInactive());
    }, []);

    const showGuidedForm = useCallback(() => {
        dispatch(GuidedJobModel.actionCreators.open());
    }, []);

    const showJobTablet = useCallback(event => {
        const {value} = event.currentTarget.dataset;
        dispatch(JobModel.actionCreators.showTablet(value));
    }, []);


    return (
        <>
            <section className="mainPage-header">
                <JobQueueHeader text={t(`guidedJob:label.vaultJob`)} aria-label={t(`aria:hiddenAssistText.addGuidedVaultJob`)}
                    canQueueFeatures={[applicationFeatures.ADD_VAULT_JOB]} showJobForm={showGuidedForm}
                    jobsToArchive={jobsToArchive} isLoading={isLoading}/>
            </section>

            <section className="mainPage-content" id={'vaultJobDisplay'}>
                <LoadingWrapper isLoading={isLoading}>
                    <div className="display-table">
                        <div className="table-header-group">
                            <TableLabelHeader label={t('common:label.name')}/>

                            <TableLabelHeader label={t('guidedJob:label.submittedDate')} style={{width: '8rem'}}/>

                            <TableLabelHeader label={t('guidedJob:label.startDate')} style={{width: '8rem'}}/>

                            <TableLabelHeader label={t('guidedJob:label.endDate')} style={{width: '8rem'}}/>

                            <TableLabelHeader label={t('common:label.state')} style={{width: '6rem'}}/>
                        </div>

                        <div className="table-row-group">
                            {Array.isArray(vaultJobs) && vaultJobs.map(job =>
                                <GuidedJobRow key={job.id} job={job} onClick={showJobTablet}
                                    isActive={job.id === activeJob?.id}/>
                            )}
                        </div>
                    </div>
                </LoadingWrapper>
            </section>

            {isGuidedJobFormActive &&
                <VaultGuidedJobForm/>
            }

            {!!activeJob &&
                <JobTablet jobId={activeJob?.id}/>
            }
        </>
    )
}

function GuidedJobRow(props) {
    const {
        job,
        isActive,
        onClick
    } = props;

    return (
        <TableRow value={job.id} isActive={isActive} onClick={onClick}>
            <div className="table-cell">
                <Text value={job.name} isWordWrap isBold/>
                <Text value={job.notes} isWordWrap/>
            </div>

            <TableLabelCell label={job.submittedDate}/>
            <TableLabelCell label={job.startedDate}/>
            <TableLabelCell label={job.finishedDate}/>

            <div className="table-cell">
                <JobStatusIcons {...job} size={'small'}/>
            </div>
        </TableRow>
    )
}

export default VaultJobDisplay;
